import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DelegationsContextToolbar } from '../../components/delegations/DelegationsContextToolbar';
import {
  AdminFilterSearch,
  AdminFilterSelect,
  AdminFiltersPanel,
} from '../../components/admin/AdminDataFilters';
import LoadingState, { Spinner } from '../../components/common/LoadingState';
import { ownerOutreachService } from '../../services/ownerOutreachService';
import {
  OWNER_OUTREACH_STATUSES,
  OwnerOutreachRow,
  OwnerOutreachStatus,
  OwnerOutreachSummary,
} from '../../types/ownerOutreach';
import { formatDateTimeEsCO } from '../../utils/dateTime';
import { notifyError, notifySuccess, notifyWarning } from '../../utils/notificationCenter';
import {
  ClipboardList,
  Expand,
  FileCheck2,
  Phone,
  PhoneCall,
  PhoneOff,
  StickyNote,
  UserCheck,
  Users,
  X,
} from 'lucide-react';

interface OwnerOutreachPageProps {
  mode: 'admin' | 'manager';
}

type DelegationFilter = 'all' | 'delegated' | 'not_delegated';

const emptySummary: OwnerOutreachSummary = {
  totalOwners: 0,
  pendingCount: 0,
  contactedCount: 0,
  unreachableCount: 0,
  willAttendCount: 0,
  delegatedCount: 0,
  contactedThenDelegatedCount: 0,
  needsContactCount: 0,
  totalCoefficient: 0,
  pendingCoefficient: 0,
  contactedCoefficient: 0,
  unreachableCoefficient: 0,
  willAttendCoefficient: 0,
  delegatedCoefficient: 0,
  contactedThenDelegatedCoefficient: 0,
  needsContactCoefficient: 0,
  pendingContactPercent: 0,
};

const statusConfig: Record<
  OwnerOutreachStatus,
  {
    label: string;
    className: string;
    selectClassName: string;
    chipClassName: string;
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  [OwnerOutreachStatus.PENDING]: {
    label: 'Sin contactar',
    className: 'bg-slate-100 text-slate-700',
    selectClassName: 'border-slate-300',
    chipClassName: 'bg-slate-100 text-slate-700',
    icon: Users,
  },
  [OwnerOutreachStatus.CONTACTED]: {
    label: 'Contactado',
    className: 'bg-blue-50 text-blue-700',
    selectClassName: 'border-blue-300',
    chipClassName: 'bg-blue-50 text-blue-700',
    icon: Phone,
  },
  [OwnerOutreachStatus.UNREACHABLE]: {
    label: 'Sin comunicación',
    className: 'bg-amber-50 text-amber-800',
    selectClassName: 'border-amber-300',
    chipClassName: 'bg-amber-50 text-amber-800',
    icon: PhoneOff,
  },
  [OwnerOutreachStatus.WILL_ATTEND]: {
    label: 'Asistirá',
    className: 'bg-emerald-50 text-emerald-700',
    selectClassName: 'border-emerald-300',
    chipClassName: 'bg-emerald-50 text-emerald-700',
    icon: UserCheck,
  },
};

const formatCoefficient = (value: number): string =>
  value.toLocaleString('es-CO', { maximumFractionDigits: 6 });

export const OwnerOutreachPage: React.FC<OwnerOutreachPageProps> = ({ mode }) => {
  const [loading, setLoading] = useState(true);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [complexes, setComplexes] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedComplexId, setSelectedComplexId] = useState('');
  const [complexName, setComplexName] = useState<string | null>(null);
  const [assemblies, setAssemblies] = useState<
    Array<{ id: string; title: string; scheduledDate?: string }>
  >([]);
  const [selectedAssemblyId, setSelectedAssemblyId] = useState('');
  const [owners, setOwners] = useState<OwnerOutreachRow[]>([]);
  const [summary, setSummary] = useState<OwnerOutreachSummary>(emptySummary);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OwnerOutreachStatus | 'all'>('all');
  const [delegationFilter, setDelegationFilter] = useState<DelegationFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [noteEditorUserId, setNoteEditorUserId] = useState<string | null>(null);
  const [noteEditorDraft, setNoteEditorDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [exporting, setExporting] = useState(false);

  const effectiveComplexId = selectedComplexId;
  const noteEditorOwner = useMemo(
    () => owners.find((owner) => owner.userId === noteEditorUserId) ?? null,
    [owners, noteEditorUserId]
  );

  const loadAssemblies = useCallback(
    async (complexId?: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await ownerOutreachService.getAssemblies(complexId);
        setComplexes(data.complexes);
        setComplexName(data.complexName);
        setAssemblies(
          data.assemblies.map((assembly) => ({
            id: assembly.id,
            title: assembly.title,
            scheduledDate: assembly.scheduledDate
              ? String(assembly.scheduledDate).slice(0, 10)
              : undefined,
          }))
        );

        if (mode === 'manager' && data.complexId) {
          setSelectedComplexId(data.complexId);
        }

        if (data.assemblies.length > 0) {
          setSelectedAssemblyId((current) => {
            if (current && data.assemblies.some((assembly) => assembly.id === current)) {
              return current;
            }
            return data.assemblies[0].id;
          });
        } else {
          setSelectedAssemblyId('');
          setOwners([]);
          setSummary(emptySummary);
        }
      } catch (err: any) {
        setError(err.message || 'No se pudo cargar el contexto de seguimiento');
      } finally {
        setLoading(false);
      }
    },
    [mode]
  );

  const loadOwners = useCallback(async (assemblyId: string, complexId?: string) => {
    if (!assemblyId || !complexId) {
      setOwners([]);
      setSummary(emptySummary);
      setCurrentPage(1);
      return;
    }

    setLoadingOwners(true);
    try {
      const data = await ownerOutreachService.listOwners(assemblyId, { complexId });
      setOwners(data.owners);
      setSummary(data.summary);
      setNoteDrafts(
        Object.fromEntries(data.owners.map((owner) => [owner.userId, owner.notes || '']))
      );
      setCurrentPage(1);
    } catch (err: any) {
      notifyError(err.message || 'Error al cargar propietarios');
      setOwners([]);
      setSummary(emptySummary);
      setCurrentPage(1);
    } finally {
      setLoadingOwners(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'manager') {
      loadAssemblies();
    }
  }, [mode, loadAssemblies]);

  useEffect(() => {
    if (mode === 'admin' && !selectedComplexId) {
      loadAssemblies();
    }
  }, [mode, selectedComplexId, loadAssemblies]);

  useEffect(() => {
    if (mode === 'admin' && selectedComplexId) {
      loadAssemblies(selectedComplexId);
    }
  }, [mode, selectedComplexId, loadAssemblies]);

  useEffect(() => {
    if (selectedAssemblyId && effectiveComplexId) {
      loadOwners(selectedAssemblyId, effectiveComplexId);
    }
  }, [selectedAssemblyId, effectiveComplexId, loadOwners]);

  const filteredOwners = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return owners.filter((owner) => {
      const matchesStatus = statusFilter === 'all' || owner.status === statusFilter;
      if (!matchesStatus) return false;

      if (delegationFilter === 'delegated' && !owner.hasActiveDelegation) return false;
      if (delegationFilter === 'not_delegated' && owner.hasActiveDelegation) return false;

      if (!term) return true;
      const haystack = [
        owner.firstName,
        owner.lastName,
        owner.email,
        owner.apartment || '',
        owner.phone || '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [owners, searchTerm, statusFilter, delegationFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredOwners.length / itemsPerPage));
  const paginatedOwners = filteredOwners.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, delegationFilter, itemsPerPage]);

  const handleRefresh = async () => {
    if (effectiveComplexId) {
      await loadAssemblies(mode === 'admin' ? effectiveComplexId : undefined);
      if (selectedAssemblyId) {
        await loadOwners(selectedAssemblyId, effectiveComplexId);
      }
    } else {
      await loadAssemblies();
    }
  };

  const handleExportExcel = useCallback(async () => {
    if (filteredOwners.length === 0) {
      notifyWarning('No hay propietarios para exportar con los filtros actuales');
      return;
    }

    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const rows = filteredOwners.map((owner) => ({
        Apartamento: owner.apartment || '',
        Nombre: `${owner.firstName} ${owner.lastName}`.trim(),
        Correo: owner.email || '',
        Teléfono: owner.phone || '',
        Coeficiente: Number(owner.coefficient || 0),
        'Estado contacto': statusConfig[owner.status].label,
        'Poder activo': owner.hasActiveDelegation ? 'Sí' : 'No',
        'Poder aceptado': owner.hasAcceptedDelegation ? 'Sí' : 'No',
        'Coef. poder aceptado': Number(owner.acceptedDelegationCoefficient || 0),
        Notas: (noteDrafts[owner.userId] ?? owner.notes ?? '').trim(),
        Actualizado: formatDateTimeEsCO(owner.updatedAt, ''),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = [
        { wch: 16 },
        { wch: 24 },
        { wch: 28 },
        { wch: 16 },
        { wch: 14 },
        { wch: 16 },
        { wch: 12 },
        { wch: 14 },
        { wch: 18 },
        { wch: 40 },
        { wch: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Seguimiento');

      const selectedAssembly = assemblies.find((assembly) => assembly.id === selectedAssemblyId);
      const sanitize = (value: string) =>
        value
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9_-]+/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
          .slice(0, 40) || 'sin_nombre';

      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(
        now.getDate()
      ).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(
        now.getMinutes()
      ).padStart(2, '0')}`;
      const complexSlug = sanitize(complexName || selectedComplexId || 'conjunto');
      const assemblySlug = sanitize(selectedAssembly?.title || selectedAssemblyId || 'asamblea');

      XLSX.writeFile(workbook, `seguimiento_${complexSlug}_${assemblySlug}_${timestamp}.xlsx`);
      notifySuccess(`Excel generado (${filteredOwners.length} filas)`);
    } catch (err) {
      console.error('Error exportando seguimiento a Excel:', err);
      notifyError('No se pudo generar el archivo Excel');
    } finally {
      setExporting(false);
    }
  }, [
    filteredOwners,
    noteDrafts,
    assemblies,
    selectedAssemblyId,
    complexName,
    selectedComplexId,
  ]);

  const handleStatusChange = async (owner: OwnerOutreachRow, nextStatus: OwnerOutreachStatus) => {
    if (!selectedAssemblyId || !effectiveComplexId) return;
    if (owner.hasActiveDelegation) return;

    setSavingUserId(owner.userId);
    try {
      await ownerOutreachService.updateOwnerStatus(
        selectedAssemblyId,
        owner.userId,
        {
          status: nextStatus,
          notes: noteDrafts[owner.userId] ?? owner.notes ?? null,
        },
        effectiveComplexId
      );
      notifySuccess('Estado actualizado');
      const refreshed = await ownerOutreachService.listOwners(selectedAssemblyId, {
        complexId: effectiveComplexId,
      });
      setSummary(refreshed.summary);
      setOwners(refreshed.owners);
    } catch (err: any) {
      notifyError(err.message || 'No se pudo actualizar el estado');
    } finally {
      setSavingUserId(null);
    }
  };

  const openNoteEditor = (owner: OwnerOutreachRow) => {
    setNoteEditorUserId(owner.userId);
    setNoteEditorDraft(noteDrafts[owner.userId] ?? owner.notes ?? '');
  };

  const closeNoteEditor = () => {
    if (savingNote) return;
    setNoteEditorUserId(null);
    setNoteEditorDraft('');
  };

  const handleSaveNote = async () => {
    if (!selectedAssemblyId || !effectiveComplexId || !noteEditorOwner) return;
    if (noteEditorOwner.hasActiveDelegation) {
      closeNoteEditor();
      return;
    }

    const nextNotes = noteEditorDraft.trim();
    const previousNotes = (noteEditorOwner.notes || '').trim();
    if (nextNotes === previousNotes) {
      closeNoteEditor();
      return;
    }

    setSavingNote(true);
    setSavingUserId(noteEditorOwner.userId);
    try {
      const updated = await ownerOutreachService.updateOwnerStatus(
        selectedAssemblyId,
        noteEditorOwner.userId,
        {
          status: noteEditorOwner.status,
          notes: nextNotes || null,
        },
        effectiveComplexId
      );
      setOwners((current) =>
        current.map((row) =>
          row.userId === noteEditorOwner.userId ? { ...row, ...updated } : row
        )
      );
      setNoteDrafts((current) => ({
        ...current,
        [noteEditorOwner.userId]: nextNotes,
      }));
      notifySuccess('Nota guardada');
      setNoteEditorUserId(null);
      setNoteEditorDraft('');
    } catch (err: any) {
      notifyError(err.message || 'No se pudo guardar la nota');
    } finally {
      setSavingNote(false);
      setSavingUserId(null);
    }
  };

  useEffect(() => {
    if (!noteEditorUserId) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (savingNote) return;
      setNoteEditorUserId(null);
      setNoteEditorDraft('');
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [noteEditorUserId, savingNote]);

  if (loading && complexes.length === 0 && assemblies.length === 0) {
    return <LoadingState layout="page" message="Cargando seguimiento..." />;
  }

  return (
    <div className="p-4 pt-16 md:p-8 md:pt-8">
      <div className="mx-auto max-w-7xl space-y-5">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">Seguimiento</h1>
        <p className="mt-2 text-gray-600">
          Control de contacto con propietarios y seguimiento de quienes ya cedieron poder.
        </p>
      </div>

      <DelegationsContextToolbar
        mode={mode}
        complexName={complexName}
        complexes={complexes}
        selectedComplexId={selectedComplexId}
        onComplexChange={setSelectedComplexId}
        assemblies={assemblies}
        selectedAssemblyId={selectedAssemblyId}
        onAssemblyChange={setSelectedAssemblyId}
        onRefresh={handleRefresh}
        refreshing={loading || loadingOwners}
        onExport={handleExportExcel}
        exportDisabled={!selectedAssemblyId || !effectiveComplexId || filteredOwners.length === 0}
        exporting={exporting}
        title="Seguimiento de propietarios"
        helperText={
          mode === 'admin'
            ? 'Selecciona conjunto y asamblea para gestionar el seguimiento.'
            : 'Gestiona el seguimiento de contacto por asamblea en tu conjunto.'
        }
        ariaLabel="Contexto de seguimiento"
      />

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {!selectedAssemblyId || !effectiveComplexId ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
          {mode === 'admin'
            ? 'Selecciona un conjunto y una asamblea para ver el seguimiento.'
            : 'Selecciona una asamblea para ver el seguimiento.'}
        </div>
      ) : (
        <>
          <AdminFiltersPanel columns={4}>
            <AdminFilterSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por apartamento, nombre o teléfono"
            />
            <AdminFilterSelect
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as OwnerOutreachStatus | 'all')}
            >
              <option value="all">Todos los contactos</option>
              {OWNER_OUTREACH_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {statusConfig[status].label}
                </option>
              ))}
            </AdminFilterSelect>
            <AdminFilterSelect
              value={delegationFilter}
              onChange={(value) => setDelegationFilter(value as DelegationFilter)}
            >
              <option value="all">Poder: todos</option>
              <option value="delegated">Solo cedió poder</option>
              <option value="not_delegated">Sin poder activo</option>
            </AdminFilterSelect>
            <AdminFilterSelect
              value={String(itemsPerPage)}
              onChange={(value) => setItemsPerPage(Number(value))}
            >
              <option value="10">10 por página</option>
              <option value="25">25 por página</option>
              <option value="50">50 por página</option>
              <option value="100">100 por página</option>
            </AdminFilterSelect>
          </AdminFiltersPanel>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <Phone className="h-4 w-4" />
                Faltan por contactar
              </div>
              <div className="mt-2 text-2xl font-semibold text-blue-950">
                {summary.needsContactCount}
              </div>
              <p className="mt-1 text-xs text-blue-700">
                Coef. {formatCoefficient(summary.needsContactCoefficient)} ·{' '}
                {summary.pendingContactPercent.toFixed(1)}% del universo
              </p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
              <div className="flex items-center gap-2 text-sm text-indigo-700">
                <FileCheck2 className="h-4 w-4" />
                Ya con poder
              </div>
              <div className="mt-2 text-2xl font-semibold text-indigo-950">
                {summary.delegatedCount}
              </div>
              <p className="mt-1 text-xs text-indigo-700">
                Coef. {formatCoefficient(summary.delegatedCoefficient)}
              </p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <PhoneCall className="h-4 w-4" />
                Contactados que cedieron
              </div>
              <div className="mt-2 text-2xl font-semibold text-emerald-950">
                {summary.contactedThenDelegatedCount}
              </div>
              <p className="mt-1 text-xs text-emerald-700">
                Coef. {formatCoefficient(summary.contactedThenDelegatedCoefficient)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Users className="h-4 w-4" />
                Universo del conjunto
              </div>
              <div className="mt-2 text-2xl font-semibold text-slate-950">
                {summary.totalOwners}
              </div>
              <p className="mt-1 text-xs text-slate-600">
                Coef. total {formatCoefficient(summary.totalCoefficient)}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2" aria-label="Conteo por estado de contacto">
            {OWNER_OUTREACH_STATUSES.map((status) => {
              const config = statusConfig[status];
              const Icon = config.icon;
              const count =
                status === OwnerOutreachStatus.PENDING
                  ? summary.pendingCount
                  : status === OwnerOutreachStatus.CONTACTED
                    ? summary.contactedCount
                    : status === OwnerOutreachStatus.UNREACHABLE
                      ? summary.unreachableCount
                      : summary.willAttendCount;
              return (
                <span
                  key={status}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm ${config.chipClassName}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {config.label}
                  <span className="font-semibold">{count}</span>
                </span>
              );
            })}
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <ClipboardList className="h-4 w-4 text-blue-600" />
                {filteredOwners.length} propietarios
                {loadingOwners ? <Spinner size="sm" /> : null}
              </div>
              <p className="text-xs text-gray-500">
                Total coef. en seguimiento: {formatCoefficient(summary.totalCoefficient)}
              </p>
            </div>

            {paginatedOwners.length === 0 ? (
              <div className="py-16 text-center text-sm text-gray-500">
                No hay propietarios que coincidan con los filtros.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Apartamento</th>
                      <th className="px-4 py-3">Nombre</th>
                      <th className="px-4 py-3">Teléfono</th>
                      <th className="px-4 py-3">Estado contacto</th>
                      <th className="px-4 py-3">Notas</th>
                      <th className="px-4 py-3">Actualizado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedOwners.map((owner) => {
                      const config = statusConfig[owner.status];
                      const isSaving = savingUserId === owner.userId;
                      const isDelegated = owner.hasActiveDelegation === true;
                      return (
                        <tr key={owner.userId} className="hover:bg-gray-50/70">
                          <td className="px-4 py-3 font-medium text-gray-800">
                            {owner.apartment || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">
                              {owner.firstName} {owner.lastName}
                            </div>
                            <div className="text-xs text-gray-500">{owner.email}</div>
                            {isDelegated ? (
                              <div className="mt-1 inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                                Cedió poder
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{owner.phone || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-2">
                                <select
                                  value={owner.status}
                                  disabled={isSaving || isDelegated}
                                  onChange={(e) =>
                                    handleStatusChange(
                                      owner,
                                      e.target.value as OwnerOutreachStatus
                                    )
                                  }
                                  className={`rounded-lg border bg-white px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 ${config.selectClassName}`}
                                >
                                  {OWNER_OUTREACH_STATUSES.map((status) => (
                                    <option key={status} value={status}>
                                      {statusConfig[status].label}
                                    </option>
                                  ))}
                                </select>
                                {isSaving ? <Spinner size="sm" /> : null}
                              </div>
                              {isDelegated ? (
                                <p className="text-[11px] text-gray-500">
                                  Estado bloqueado mientras el poder esté activo
                                </p>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const noteText = (
                                noteDrafts[owner.userId] ??
                                owner.notes ??
                                ''
                              ).trim();
                              const canEditNote = !isSaving && !isDelegated;
                              return (
                                <button
                                  type="button"
                                  onClick={() => openNoteEditor(owner)}
                                  disabled={isSaving}
                                  className={`group flex w-full min-w-[180px] max-w-[260px] items-start gap-2 rounded-lg border px-2.5 py-2 text-left transition ${
                                    noteText
                                      ? 'border-slate-200 bg-slate-50/80 hover:border-blue-200 hover:bg-blue-50/50'
                                      : 'border-dashed border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/40'
                                  } disabled:opacity-60`}
                                  title={
                                    isDelegated
                                      ? 'Ver nota'
                                      : noteText
                                        ? 'Ver y editar nota'
                                        : 'Añadir nota'
                                  }
                                >
                                  <StickyNote
                                    className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
                                      noteText ? 'text-blue-600' : 'text-slate-400'
                                    }`}
                                  />
                                  <span className="min-w-0 flex-1">
                                    {noteText ? (
                                      <span className="line-clamp-2 whitespace-pre-wrap text-xs leading-relaxed text-slate-700">
                                        {noteText}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-slate-400">
                                        {isDelegated ? 'Sin nota' : 'Añadir nota…'}
                                      </span>
                                    )}
                                  </span>
                                  <Expand
                                    className={`mt-0.5 h-3.5 w-3.5 shrink-0 opacity-0 transition group-hover:opacity-100 ${
                                      canEditNote || noteText
                                        ? 'text-slate-400 group-hover:text-blue-600'
                                        : 'text-slate-300'
                                    }`}
                                  />
                                </button>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {owner.updatedAt ? formatDateTimeEsCO(owner.updatedAt) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {filteredOwners.length > itemsPerPage ? (
              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
                <span>
                  Página {currentPage} de {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}
      </div>

      {noteEditorOwner ? (
        <div className="fixed inset-0 z-50 flex justify-end" role="presentation">
          <button
            type="button"
            aria-label="Cerrar nota"
            className="absolute inset-0 bg-slate-900/35 backdrop-blur-[1px] transition-opacity"
            onClick={closeNoteEditor}
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="outreach-note-title"
            className="relative flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl animate-[slideInRight_180ms_ease-out]"
          >
            <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-5 pb-4 pt-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700/80">
                    Nota de seguimiento
                  </p>
                  <h2
                    id="outreach-note-title"
                    className="mt-1 text-lg font-semibold tracking-tight text-slate-900"
                  >
                    {noteEditorOwner.firstName} {noteEditorOwner.lastName}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Apto. {noteEditorOwner.apartment || '—'}
                    {noteEditorOwner.phone ? ` · ${noteEditorOwner.phone}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeNoteEditor}
                  disabled={savingNote}
                  className="rounded-lg border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
                  aria-label="Cerrar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
              <label
                htmlFor="outreach-note-textarea"
                className="mb-2 text-xs font-medium text-slate-600"
              >
                {noteEditorOwner.hasActiveDelegation
                  ? 'Solo lectura (poder activo)'
                  : 'Escribe con espacio completo; se guarda al confirmar'}
              </label>
              <textarea
                id="outreach-note-textarea"
                autoFocus
                value={noteEditorDraft}
                onChange={(e) => setNoteEditorDraft(e.target.value)}
                disabled={savingNote || noteEditorOwner.hasActiveDelegation}
                readOnly={noteEditorOwner.hasActiveDelegation}
                placeholder="Detalle de la llamada, compromiso, próximo contacto…"
                className="min-h-[280px] flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm leading-relaxed text-slate-800 shadow-inner outline-none transition focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-500/30 disabled:opacity-70"
              />
              <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-400">
                <span>
                  {noteEditorDraft.trim().length === 0
                    ? 'Sin contenido'
                    : `${noteEditorDraft.trim().length} caracteres`}
                </span>
                {noteEditorOwner.updatedAt ? (
                  <span>Actualizado {formatDateTimeEsCO(noteEditorOwner.updatedAt)}</span>
                ) : null}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-5 py-4">
              <button
                type="button"
                onClick={closeNoteEditor}
                disabled={savingNote}
                className="rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                {noteEditorOwner.hasActiveDelegation ? 'Cerrar' : 'Cancelar'}
              </button>
              {!noteEditorOwner.hasActiveDelegation ? (
                <button
                  type="button"
                  onClick={handleSaveNote}
                  disabled={savingNote}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingNote ? <Spinner size="sm" /> : null}
                  Guardar nota
                </button>
              ) : null}
            </div>
          </aside>
          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(16px); opacity: 0.85; }
              to { transform: translateX(0); opacity: 1; }
            }
          `}</style>
        </div>
      ) : null}
    </div>
  );
};

export default OwnerOutreachPage;
